import { Fragment, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { COLLECTIONS } from '../../collections';
import { CATEGORIES, PAY_TYPES } from '../../constants';
import { formatCurrency, pctChange, formatDate, statementTiming } from '../../utils/formatters';
import { computedTotalCash, targetPctOfSalary } from '../../utils/compensation';
import { cn } from '../../utils/cn';

/* ── Equity grant category definitions ── */
const EQUITY_CATEGORIES = {
  promotion: {
    label: 'Promotion Equity',
    description: 'Given to recognize an increase in your responsibilities and to recognize promotion to the next level.',
  },
  performance: {
    label: 'Performance Equity',
    description: 'Given to recognize your specific contribution to the business.',
  },
  refresh: {
    label: 'Refresh Equity',
    description: 'Given to maintain your Forter ownership position.',
  },
};

/* ── Animation helpers ── */
const stagger = {
  visible: { transition: { staggerChildren: 0.06 } },
};
const pop = {
  hidden: { opacity: 0, y: 20, scale: 0.97 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] } },
};

export default function PresentView() {
  const { cycleId, compId } = useParams();
  const nav = useNavigate();
  const [comp, setComp] = useState(null);
  const [cycle, setCycle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [compSnap, cycleSnap] = await Promise.all([
          getDoc(doc(db, COLLECTIONS.cycles, cycleId, COLLECTIONS.compensations, compId)),
          getDoc(doc(db, COLLECTIONS.cycles, cycleId)),
        ]);
        if (compSnap.exists()) setComp({ id: compSnap.id, ...compSnap.data() });
        if (cycleSnap.exists()) setCycle({ id: cycleSnap.id, ...cycleSnap.data() });
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    })();
  }, [cycleId, compId]);

  useEffect(() => {
    const handler = (e) => e.key === 'Escape' && nav(-1);
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [nav]);

  if (loading) return <LoadingSkeleton />;
  if (!comp) return (
    <div className="h-screen bg-navy-950 flex items-center justify-center text-white">
      <p className="text-white/60">Compensation not found</p>
    </div>
  );

  const cat = CATEGORIES[comp.category] || { label: comp.category };
  const hasPromo = comp.newTitle && comp.newTitle !== comp.currentTitle;
  const hasVariable = !!(comp.currentVariableTarget || comp.newVariableTarget);
  const payTypeLabel = PAY_TYPES.find((p) => p.value === comp.payType)?.label || 'Annual';
  const currentTotalCash = comp.totalCurrentComp ?? computedTotalCash(comp, 'current');
  const newTotalCash = comp.totalNewComp ?? computedTotalCash(comp, 'new');

  // Build category description for intro blurb (single category only)
  const categoryDesc = (() => {
    if (comp.category === 'promotion') return 'a promotion';
    if (comp.category === 'role_change') return 'a role change';
    if (comp.category === 'merit_increase') return 'a merit increase';
    if (comp.category === 'equity_grant') return 'an equity grant';
    if (comp.category === 'bonus_change') return 'a bonus change';
    return cat.label?.toLowerCase();
  })();

  // Group equity grants by category, filter out zero-unit grants
  const equityByCategory = (comp.equityGrants || [])
    .filter((g) => g.units > 0)
    .reduce((acc, grant) => {
      const key = grant.grantCategory || grant.category || 'refresh';
      if (!acc[key]) acc[key] = [];
      acc[key].push(grant);
      return acc;
    }, {});

  const hasEquity = Object.keys(equityByCategory).length > 0;

  return (
    <div className="h-screen bg-navy-950 text-white overflow-y-auto flex flex-col">
      {/* Ambient background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-accent-600/10 rounded-full blur-[140px] animate-float" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[400px] h-[400px] bg-cyan-500/8 rounded-full blur-[120px] animate-float" style={{ animationDelay: '3s' }} />
      </div>

      {/* ── Top bar ── */}
      <div className="relative z-50 flex items-center justify-between px-6 pt-5 pb-2 shrink-0 print:hidden">
        <button
          onClick={() => nav(-1)}
          className="flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors backdrop-blur-md bg-white/5 rounded-full px-4 py-2 border border-white/[0.08]"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
          </svg>
          Back
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 text-sm text-white/50 hover:text-white transition-colors backdrop-blur-md bg-white/5 rounded-full px-4 py-2 border border-white/[0.08]"
            title="Export as PDF"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
            </svg>
            Export PDF
          </button>
          <span className="text-[11px] text-white/30 backdrop-blur-md bg-white/5 rounded-full px-3 py-1.5 border border-white/[0.06]">
            ESC to exit
          </span>
        </div>
      </div>

      {/* ── Main content ── */}
      <motion.div
        variants={stagger}
        initial="hidden"
        animate="visible"
        className="relative z-10 flex-1 flex flex-col px-6 pb-10 max-w-3xl mx-auto w-full"
      >
        {/* ── Header: Name, Title, Department ── */}
        <motion.div variants={pop} className="text-center pt-8 pb-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent-400/80 mb-2">
            {cycle?.name || 'Compensation Review'}
          </p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">{comp.employeeName}</h1>
          <div className="flex items-center justify-center gap-2 mt-1.5 text-sm text-white/40">
            <span>{comp.currentTitle}</span>
            {comp.department && (
              <>
                <span className="w-1 h-1 rounded-full bg-white/20" />
                <span>{comp.department}</span>
              </>
            )}
          </div>

          {/* Category + Effective Date chips — moved to top */}
          <div className="flex flex-wrap items-center justify-center gap-2.5 mt-4">
            <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold bg-accent-500/15 text-accent-300 border border-accent-500/20">
              {cat.label}
            </span>
            {comp.effectiveDate && (
              <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold bg-white/[0.06] text-white/50 border border-white/[0.06]">
                <svg className="w-3 h-3 text-white/30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                </svg>
                Effective {formatDate(comp.effectiveDate)}
              </span>
            )}
          </div>
        </motion.div>

        {/* ── Intro blurb ── */}
        <motion.div variants={pop}>
          <GlassCard className="text-center">
            <p className="text-sm text-white/50 leading-relaxed">
              In appreciation of the professionalism, quality of work and considerable effort that you have demonstrated at Forter, we are happy to inform you that you have been awarded{' '}
              <span className="text-accent-300 font-semibold">{categoryDesc}</span>.
              {statementTiming(comp.effectiveDate, comp.payDate).map((sentence) => (
                <Fragment key={sentence}> <span className="text-white/70 font-semibold">{sentence}</span></Fragment>
              ))}
            </p>
          </GlassCard>
        </motion.div>

        {/* ── Before / After comparison table ── */}
        <motion.div variants={pop} className="mt-4">
          <GlassCard>
            <div className="mb-3">
              <h3 className="text-sm font-bold text-white/90">Details</h3>
              <p className="text-[11px] text-white/40 leading-relaxed mt-0.5">Details are as follows:</p>
            </div>
            <div className="overflow-hidden rounded-xl border border-white/[0.06]">
              <table className="w-full table-fixed text-sm">
                <thead>
                  <tr className="bg-white/[0.03]">
                    <th className="text-left py-2.5 px-2 sm:px-4 text-[10px] font-bold uppercase tracking-[0.12em] text-white/30 w-[38%]" />
                    <th className="text-left py-2.5 px-2 sm:px-4 text-[10px] font-bold uppercase tracking-[0.12em] text-white/30 w-[27%]">Before</th>
                    <th className="text-left py-2.5 px-2 sm:px-4 text-[10px] font-bold uppercase tracking-[0.12em] text-accent-400/70 w-[35%]">
                      As of {comp.effectiveDate ? formatDate(comp.effectiveDate) : '—'}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.04]">
                  {/* Job Title row — only show if changed */}
                  {hasPromo && (
                    <tr>
                      <td className="py-3 px-2 sm:px-4 text-xs font-semibold text-white/50">Job Title</td>
                      <td className="py-3 px-2 sm:px-4 text-xs text-white/40">{comp.currentTitle}</td>
                      <td className="py-3 px-2 sm:px-4 text-xs font-semibold text-white/90">{comp.newTitle}</td>
                    </tr>
                  )}
                  {/* Salary row */}
                  <tr>
                    <td className="py-3 px-2 sm:px-4 text-xs font-semibold text-white/50">
                      {payTypeLabel} Salary
                      <span className="text-white/25 ml-1">({comp.currency || 'USD'})</span>
                    </td>
                    <td className="py-3 px-2 sm:px-4 text-xs text-white/40">{formatCurrency(comp.currentBase, comp.currency)}</td>
                    <td className="py-3 px-2 sm:px-4">
                      <span className="text-xs font-semibold text-white/90">{formatCurrency(comp.newBase, comp.currency)}</span>
                      {comp.currentBase > 0 && comp.newBase !== comp.currentBase && (
                        <span className={cn(
                          'block sm:inline sm:ml-2 text-[10px] font-bold',
                          comp.newBase >= comp.currentBase ? 'text-emerald-400' : 'text-red-400',
                        )}>
                          {pctChange(comp.currentBase, comp.newBase)}
                        </span>
                      )}
                    </td>
                  </tr>
                  {/* Variable row */}
                  {hasVariable && (
                    <tr>
                      <td className="py-3 px-2 sm:px-4 text-xs font-semibold text-white/50">Target Variable</td>
                      <td className="py-3 px-2 sm:px-4 text-xs text-white/40">
                        {comp.currentVariableTarget
                          ? formatVariableTarget(comp, 'current')
                          : '—'}
                      </td>
                      <td className="py-3 px-2 sm:px-4">
                        <span className="text-xs font-semibold text-white/90">
                          {formatVariableTarget(comp, 'new')}
                        </span>
                      </td>
                    </tr>
                  )}
                  {/* Total Cash row */}
                  <tr>
                    <td className="py-3 px-2 sm:px-4 text-xs font-semibold text-white/70">Total Cash</td>
                    <td className="py-3 px-2 sm:px-4 text-xs text-white/50">{formatCurrency(currentTotalCash, comp.currency)}</td>
                    <td className="py-3 px-2 sm:px-4">
                      <span className="text-xs font-semibold text-white">{formatCurrency(newTotalCash, comp.currency)}</span>
                      {currentTotalCash > 0 && newTotalCash !== currentTotalCash && (
                        <span className={cn(
                          'block sm:inline sm:ml-2 text-[10px] font-bold',
                          newTotalCash >= currentTotalCash ? 'text-emerald-400' : 'text-red-400',
                        )}>
                          {pctChange(currentTotalCash, newTotalCash)}
                        </span>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </GlassCard>
        </motion.div>

        {/* ── Equity Sections — one per grant category ── */}
        {hasEquity && (
          <motion.div variants={pop} className="mt-4 space-y-3">
            {Object.entries(EQUITY_CATEGORIES).map(([key, meta]) => {
              const grants = equityByCategory[key];
              if (!grants || grants.length === 0) return null;
              const totalShares = grants.reduce((sum, g) => sum + (g.units || 0), 0);
              // Use the first grant's vesting info as representative
              const vestingInfo = grants[0].vestingPeriod
                ? buildVestingLabel(grants[0])
                : null;

              return (
                <GlassCard key={key}>
                  <div className="mb-3">
                    <h3 className="text-sm font-bold text-white/90">{meta.label}</h3>
                    <p className="text-[11px] text-white/40 leading-relaxed mt-0.5">{meta.description}</p>
                  </div>
                  <div className="overflow-hidden rounded-xl border border-white/[0.06]">
                    <table className="w-full text-sm">
                      <tbody className="divide-y divide-white/[0.04]">
                        <tr className="bg-white/[0.03]">
                          <td className="py-2.5 px-4 text-xs font-semibold text-white/50">Total RSUs Granted</td>
                          <td className="py-2.5 px-4 text-xs font-bold text-accent-300">
                            {totalShares.toLocaleString()} shares
                          </td>
                        </tr>
                        {vestingInfo && (
                          <tr>
                            <td className="py-2.5 px-4 text-xs font-semibold text-white/50">Vesting Schedule</td>
                            <td className="py-2.5 px-4 text-xs text-white/70">{vestingInfo}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </GlassCard>
              );
            })}
          </motion.div>
        )}

        {/* Bottom spacer */}
        <div className="pb-6" />
      </motion.div>
    </div>
  );
}

/* ── Helpers ── */

function formatVariableTarget(comp, period) {
  const target = comp[`${period}VariableTarget`];
  if (comp.variableDisplayType === 'percent') return `${target}%`;
  const pct = targetPctOfSalary(comp, period);
  return `${formatCurrency(target, comp.currency)}${pct === null ? '' : ` (${pct}%)`}`;
}

function buildVestingLabel(grant) {
  // If vestingText is provided, use it directly (allows custom override)
  if (grant.vestingText) return grant.vestingText;
  
  // Otherwise build from structured cliff + vestingPeriod fields
  const parts = [];
  if (grant.cliff) parts.push(`${grant.cliff} cliff`);
  if (grant.vestingPeriod) parts.push(`vests over ${grant.vestingPeriod}`);
  if (parts.length === 0) return null;
  return parts.join(', ');
}

/* ── Sub-components ── */

function GlassCard({ children, className }) {
  return (
    <div className={cn(
      'bg-white/[0.04] backdrop-blur-xl border border-white/[0.07] rounded-2xl p-5',
      className,
    )}>
      {children}
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="h-screen bg-navy-950 flex items-center justify-center">
      <div className="text-center space-y-3">
        <div className="w-10 h-10 rounded-2xl mx-auto animate-pulse" style={{ background: 'rgba(255,255,255,0.05)' }} />
        <div className="w-40 h-5 mx-auto rounded animate-pulse" style={{ background: 'rgba(255,255,255,0.05)' }} />
        <div className="w-24 h-3 mx-auto rounded animate-pulse" style={{ background: 'rgba(255,255,255,0.05)' }} />
      </div>
    </div>
  );
}
